import { useCallback, useEffect, useState } from 'react'
import { InvalidBudgetError, isRole, roleHasCapability, validateBudget } from '@qim/shared'
import { ApiError, fetchBudget, setBudget } from '../api.js'
import { formatInstant, formatMoney } from '../format.js'
import type { BudgetStatus } from '../api.js'
import type { FormEvent, JSX } from 'react'

/**
 * The platform budget over Qrvey's own fleet (IM-152).
 *
 * ## Why this loads by itself when the cost panel beside it waits for a click
 *
 * The cost panel reads every account through Cost Explorer, which charges per
 * request. This reads one item the daily evaluation already stored, and makes
 * no cross-account call at all — so there is nothing to protect by waiting.
 *
 * ## What it says, and what it does not
 *
 * What was spent, against what cap, as of when, and which accounts could not
 * be read — never counted as zero. When new instances are being refused it says
 * so first, because that is the fact somebody arriving here after a refused
 * apply came for. It recommends nothing: which instance to destroy is a person's
 * decision, and the standing no-verdict rule does not bend for a dollar figure.
 */

const canRead = (role: string): boolean => isRole(role) && roleHasCapability(role, 'cost:read')
const canSet = (role: string): boolean => isRole(role) && roleHasCapability(role, 'budget:manage')

/**
 * The budget read, as a hook — so the fleet's Cost tab can draw the budget line
 * on its chart and render this panel from **one** read rather than two.
 */
export const useBudget = (
  baseUrl: string,
  role: string,
): {
  readonly status: BudgetStatus | undefined
  readonly failure: string | undefined
  readonly reload: () => Promise<void>
} => {
  const [status, setStatus] = useState<BudgetStatus | undefined>(undefined)
  const [failure, setFailure] = useState<string | undefined>(undefined)

  const reload = useCallback(async (): Promise<void> => {
    try {
      setStatus(await fetchBudget(baseUrl))
      setFailure(undefined)
    } catch {
      setFailure('Could not read the platform budget just now.')
    }
  }, [baseUrl])

  useEffect(() => {
    if (canRead(role)) void reload()
  }, [reload, role])

  return { status, failure, reload }
}

/** Reads the budget itself. Use `BudgetDetails` where the read is already in hand. */
export const PlatformBudgetPanel = ({
  baseUrl,
  role,
}: {
  readonly baseUrl: string
  readonly role: string
}): JSX.Element | null => {
  const { status, failure, reload } = useBudget(baseUrl, role)
  return (
    <BudgetDetails
      baseUrl={baseUrl}
      role={role}
      status={status}
      failure={failure}
      onSaved={() => void reload()}
    />
  )
}

export const BudgetDetails = ({
  baseUrl,
  role,
  status,
  failure,
  onSaved,
}: {
  readonly baseUrl: string
  readonly role: string
  readonly status: BudgetStatus | undefined
  readonly failure: string | undefined
  readonly onSaved: () => void
}): JSX.Element | null => {
  const [editing, setEditing] = useState(false)

  if (!canRead(role)) return null

  const budget = status?.budget
  const evaluation = status?.evaluation

  return (
    <section className="panel">
      <h2>Platform budget</h2>
      <p className="hint">
        One monthly ceiling over Qrvey&rsquo;s own accounts. Customers&rsquo; accounts are not
        counted.
      </p>

      {failure === undefined ? null : <p className="failure">{failure}</p>}

      {status?.blocking === true ? (
        <p role="alert" className="failure">
          <strong>New instances in Qrvey-owned accounts are being refused.</strong> This month is
          over the budget. Existing instances, destroys and plans are unaffected. Raise the budget
          or turn blocking off to lift it.
        </p>
      ) : null}

      {status === undefined ? null : budget === undefined ? (
        <p>No platform budget is set.</p>
      ) : (
        <dl className="detail">
          <dt>Budget</dt>
          <dd>
            {formatMoney(budget.monthlyAmount, budget.currency)} a month, warning at{' '}
            {budget.warnAtPercent}%
            <p className="muted">
              {budget.blocksNewInstances
                ? 'Refuses new instances once crossed.'
                : 'Report only: nothing is refused when it is crossed.'}{' '}
              Set by {budget.by} on {formatInstant(budget.at)}.
            </p>
          </dd>
          <dt>This month</dt>
          <dd>
            {evaluation === undefined ? (
              'Not evaluated yet — the first evaluation runs on the next weekday morning.'
            ) : (
              <>
                {formatMoney(evaluation.total, evaluation.currency)} spent, {evaluation.period.from}{' '}
                to {evaluation.period.to}
                <p className="muted">
                  Evaluated {formatInstant(evaluation.evaluatedAt)}. Cost Explorer lags about a day.
                  {evaluation.warnCrossedAt === undefined
                    ? ''
                    : ` Passed the warning line on ${formatInstant(evaluation.warnCrossedAt)}.`}
                  {evaluation.capCrossedAt === undefined
                    ? ''
                    : ` Passed the budget on ${formatInstant(evaluation.capCrossedAt)}.`}
                </p>
                {evaluation.unread.length === 0 ? null : (
                  <p className="hint">
                    Not in the total, because they could not be read:{' '}
                    {evaluation.unread.map((gap) => gap.accountKey).join(', ')}. The real figure is
                    higher by whatever they spent.
                  </p>
                )}
              </>
            )}
          </dd>
        </dl>
      )}

      {canSet(role) ? (
        editing ? (
          <BudgetForm
            baseUrl={baseUrl}
            {...(budget === undefined ? {} : { current: budget })}
            onDone={() => {
              setEditing(false)
              onSaved()
            }}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <button type="button" className="ghost" onClick={() => setEditing(true)}>
            {budget === undefined ? 'Set a budget' : 'Change the budget'}
          </button>
        )
      ) : null}
    </section>
  )
}

const BudgetForm = ({
  baseUrl,
  current,
  onDone,
  onCancel,
}: {
  readonly baseUrl: string
  readonly current?: BudgetStatus['budget']
  readonly onDone: () => void
  readonly onCancel: () => void
}): JSX.Element => {
  const [amount, setAmount] = useState(current === undefined ? '' : String(current.monthlyAmount))
  const [warn, setWarn] = useState(current === undefined ? '80' : String(current.warnAtPercent))
  const [blocks, setBlocks] = useState(current?.blocksNewInstances ?? true)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | undefined>(undefined)

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    setProblem(undefined)
    let input
    try {
      // The API's own validator, so a refusal arrives before the request with
      // the same words.
      input = validateBudget({
        monthlyAmount: Number(amount),
        warnAtPercent: Number(warn),
        blocksNewInstances: blocks,
      })
    } catch (error: unknown) {
      setProblem(
        error instanceof InvalidBudgetError ? error.message : 'Those values are not valid.',
      )
      return
    }
    setBusy(true)
    try {
      await setBudget(baseUrl, input)
      onDone()
    } catch (error: unknown) {
      setProblem(
        error instanceof ApiError && error.status === 403
          ? 'Your role does not permit that (budget:manage).'
          : 'Could not save the budget just now.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)}>
      <div className="field-row">
        <div className="field">
          <label htmlFor="budgetAmount">Monthly budget (USD)</label>
          <input
            id="budgetAmount"
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="budgetWarn">Warn at (%)</label>
          <input
            id="budgetWarn"
            inputMode="numeric"
            value={warn}
            onChange={(event) => setWarn(event.target.value)}
          />
        </div>
      </div>
      <label htmlFor="budgetBlocks" className="check">
        <input
          id="budgetBlocks"
          type="checkbox"
          checked={blocks}
          onChange={(event) => setBlocks(event.target.checked)}
        />
        Refuse new instances in Qrvey-owned accounts once it is crossed
      </label>
      <p className="hint">
        Only new instances are ever refused. Running instances, upgrades, destroys and plans go
        through. Changing this takes effect at once and is recorded in the audit trail.
      </p>
      {problem === undefined ? null : <p className="failure">{problem}</p>}
      <div className="actions">
        <button type="button" className="ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save budget'}
        </button>
      </div>
    </form>
  )
}
